/**
 * P2-08 · Recently-viewed store — pure list operations.
 *
 * Isomorphic (no client/server imports): the ordered, deduped, capped,
 * TTL-bounded list of product ids a visitor has viewed. The server signs/reads
 * it as a cookie; the client hook parses it for live updates. Kept pure so the
 * dedupe/cap/TTL/parse rules are unit-tested in one place and can't drift
 * between the two consumers.
 */
import { z } from "zod";

export const RECENTLY_VIEWED_CAP = 20;
/** Entries older than this are dropped on read (30 days). */
export const RECENTLY_VIEWED_TTL_MS = 30 * 24 * 60 * 60 * 1000;

const entrySchema = z.object({
  id: z.string().min(1),
  /** Epoch ms when the view was recorded (for TTL + ordering). */
  at: z.number().int().nonnegative(),
});
export type RvEntry = z.infer<typeof entrySchema>;
const listSchema = z.array(entrySchema);
export type RvList = RvEntry[];

/** Move `id` to the front (dedupe), then cap. Newest-first. */
export function addView(
  list: RvList,
  id: string,
  now: number,
  cap = RECENTLY_VIEWED_CAP,
): RvList {
  const withoutId = list.filter((e) => e.id !== id);
  return [{ id, at: now }, ...withoutId].slice(0, cap);
}

export function pruneExpired(
  list: RvList,
  now: number,
  ttlMs = RECENTLY_VIEWED_TTL_MS,
): RvList {
  return list.filter((e) => now - e.at < ttlMs);
}

/** The non-expired ids, newest-first. */
export function idsFrom(
  list: RvList,
  now: number,
  ttlMs = RECENTLY_VIEWED_TTL_MS,
): string[] {
  return pruneExpired(list, now, ttlMs).map((e) => e.id);
}

/** Parse raw JSON into a valid list; any corruption resets to empty. */
export function parseList(json: string | null | undefined): RvList {
  if (!json) return [];
  try {
    const parsed = listSchema.safeParse(JSON.parse(json));
    return parsed.success ? parsed.data : [];
  } catch {
    return [];
  }
}

export function serializeList(list: RvList): string {
  return JSON.stringify(list);
}
